import { useEffect, useState, FormEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  PawPrint,
  CalendarCheck,
  Stethoscope,
  Syringe,
  Pill,
  FileText,
  Sparkles,
  Home,
  Footprints,
  GraduationCap,
  BedDouble,
  ClipboardList,
  Link2,
  Check,
  Award,
  Plus,
  Pencil,
  Trash2,
  Bell,
  UserX,
  Printer,
  Mail,
  Loader2,
} from "lucide-react";
import { api, mensajeError } from "../api/client";
import { Spinner } from "../components/ui/Spinner";
import { Badge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { EmptyState } from "../components/ui/EmptyState";
import { CrudPage, aFechaSoloDiaInput } from "../components/crud/CrudPage";
import { ModalBajaMascota } from "../components/mascotas/ModalBajaMascota";
import { useOpciones } from "../hooks/useOpciones";

const TABS = [
  { id: "consultas", label: "Consultas" },
  { id: "vacunas", label: "Vacunas" },
  { id: "recetas", label: "Recetas" },
  { id: "documentos", label: "Documentos" },
  { id: "certificado-salud", label: "Certificado de salud" },
];

interface EventoExpediente {
  id: string;
  tipo: string;
  titulo: string;
  detalle?: string | null;
  fecha: string;
  estado?: string | null;
  responsable?: string | null;
  vacunaId?: string;
  certificadoDisponible?: boolean;
}

const ICONOS_EVENTO: Record<string, any> = {
  Cita: CalendarCheck,
  Consulta: Stethoscope,
  Vacuna: Syringe,
  Receta: Pill,
  Documento: FileText,
  Estética: Sparkles,
  "A domicilio": Home,
  Paseo: Footprints,
  Entrenamiento: GraduationCap,
  Hospedaje: BedDouble,
};

const COLORES_EVENTO: Record<string, string> = {
  Cita: "bg-sky-50 text-sky-600",
  Consulta: "bg-brand-50 text-brand-600",
  Vacuna: "bg-violet-50 text-violet-600",
  Receta: "bg-amber-50 text-amber-600",
  Documento: "bg-slate-100 text-slate-600",
  Estética: "bg-pink-50 text-pink-600",
  "A domicilio": "bg-emerald-50 text-emerald-600",
  Paseo: "bg-lime-50 text-lime-700",
  Entrenamiento: "bg-indigo-50 text-indigo-600",
  Hospedaje: "bg-orange-50 text-orange-600",
};

const COLORES_ESTADO_BADGE: Record<string, "verde" | "amarillo" | "rojo" | "azul" | "gris" | "morado"> = {
  COMPLETADO: "verde",
  ENVIADO: "verde",
  CONFIRMADA: "azul",
  PENDIENTE: "amarillo",
  PROGRAMADO: "amarillo",
  EN_PROCESO: "azul",
  ACTIVA: "azul",
  RESERVADO: "amarillo",
  CANCELADA: "rojo",
  CANCELADO: "rojo",
  NO_ASISTIO: "rojo",
};

interface MedicamentoReceta {
  nombre: string;
  dosis: string;
  frecuencia: string;
  duracion: string;
}

const MEDICAMENTO_VACIO: MedicamentoReceta = { nombre: "", dosis: "", frecuencia: "", duracion: "" };

// Campo "custom" para el formulario de recetas (ver CrudPage.tsx): permite
// recetar más de un medicamento a la vez, cada uno con su propia dosis,
// frecuencia y duración, en vez de limitar la receta a un solo medicamento.
function CampoMedicamentos({
  valores,
  onChange,
}: {
  valores: Record<string, any>;
  onChange: (campo: string, valor: any) => void;
}) {
  const medicamentos: MedicamentoReceta[] =
    Array.isArray(valores.medicamentos) && valores.medicamentos.length > 0 ? valores.medicamentos : [{ ...MEDICAMENTO_VACIO }];

  function actualizar(indice: number, campo: keyof MedicamentoReceta, valor: string) {
    const copia = medicamentos.map((m, i) => (i === indice ? { ...m, [campo]: valor } : m));
    onChange("medicamentos", copia);
  }

  function agregar() {
    onChange("medicamentos", [...medicamentos, { ...MEDICAMENTO_VACIO }]);
  }

  function quitar(indice: number) {
    const copia = medicamentos.filter((_, i) => i !== indice);
    onChange("medicamentos", copia.length > 0 ? copia : [{ ...MEDICAMENTO_VACIO }]);
  }

  return (
    <div>
      <label className="label mb-1">
        Medicamentos <span className="text-red-500">*</span>
      </label>
      <div className="space-y-3">
        {medicamentos.map((m, i) => (
          <div key={i} className="rounded-xl border border-slate-100 p-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Medicamento {i + 1}</p>
              {medicamentos.length > 1 && (
                <button type="button" onClick={() => quitar(i)} className="flex items-center gap-1 text-xs text-red-500 hover:underline">
                  <Trash2 size={12} /> Quitar
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <input
                className="input col-span-2"
                required
                placeholder="Nombre del medicamento"
                value={m.nombre}
                onChange={(e) => actualizar(i, "nombre", e.target.value)}
              />
              <input className="input" placeholder="Dosis" value={m.dosis} onChange={(e) => actualizar(i, "dosis", e.target.value)} />
              <input
                className="input"
                placeholder="Frecuencia"
                value={m.frecuencia}
                onChange={(e) => actualizar(i, "frecuencia", e.target.value)}
              />
              <input
                className="input col-span-2"
                placeholder="Duración"
                value={m.duracion}
                onChange={(e) => actualizar(i, "duracion", e.target.value)}
              />
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={agregar} className="btn-secondary mt-3 flex items-center gap-1.5 text-sm">
        <Plus size={15} /> Agregar otro medicamento
      </button>
    </div>
  );
}

export default function MascotaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mascota, setMascota] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState("consultas");
  const [expediente, setExpediente] = useState<EventoExpediente[] | null>(null);
  const [errorExpediente, setErrorExpediente] = useState<string | null>(null);
  const [linkCopiado, setLinkCopiado] = useState(false);
  const [refreshVacunas, setRefreshVacunas] = useState(0);
  const [mostrarModalBaja, setMostrarModalBaja] = useState(false);

  const [vacunaAConfirmar, setVacunaAConfirmar] = useState<any | null>(null);
  const [confirmNombre, setConfirmNombre] = useState("");
  const [confirmFecha, setConfirmFecha] = useState("");
  const [confirmGuardando, setConfirmGuardando] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // --- Vacunas: formulario a medida (no usa el CrudPage genérico) porque
  // necesita lógica condicional que ese componente no soporta: ocultar la
  // fecha de próxima dosis con un botón "No aplica", y solo mostrar la
  // opción de recordatorio automático cuando sí hay una próxima dosis
  // capturada.
  const [vacunas, setVacunas] = useState<any[]>([]);
  const [cargandoVacunas, setCargandoVacunas] = useState(true);
  const [errorVacunas, setErrorVacunas] = useState<string | null>(null);
  const [modalVacunaAbierto, setModalVacunaAbierto] = useState(false);
  const [vacunaEditando, setVacunaEditando] = useState<any | null>(null);
  const [formVacuna, setFormVacuna] = useState({
    veterinarioId: "",
    nombre: "",
    lote: "",
    farmaceutica: "",
    viaAplicacion: "",
    fechaAplicacion: "",
    sinProximaDosis: false,
    fechaProximaDosis: "",
    recordatorioAutomatico: false,
  });
  const [guardandoVacuna, setGuardandoVacuna] = useState(false);
  const [errorFormVacuna, setErrorFormVacuna] = useState<string | null>(null);

  const { opciones: veterinarios } = useOpciones("/usuarios", (u) => u.nombre, (u) => ["VETERINARIO", "ADMIN"].includes(u.rol));

  // --- Recetas: enviar por correo al cliente (además de poder verse/imprimirse) ---
  const [enviandoRecetaId, setEnviandoRecetaId] = useState<string | null>(null);

  async function enviarRecetaCorreo(id: string) {
    setEnviandoRecetaId(id);
    try {
      const res = await api.post(`/recetas/${id}/enviar-correo`);
      alert(`Receta enviada a ${res.data.correo}.`);
    } catch (err) {
      alert(mensajeError(err));
    } finally {
      setEnviandoRecetaId(null);
    }
  }

  useEffect(() => {
    if ((tab !== "vacunas" && tab !== "certificado-salud") || !mascota) return;
    setCargandoVacunas(true);
    setErrorVacunas(null);
    api
      .get("/vacunas", { params: { mascotaId: mascota.id, porPagina: 100 } })
      .then((res) => setVacunas(res.data.items ?? []))
      .catch((err) => setErrorVacunas(mensajeError(err)))
      .finally(() => setCargandoVacunas(false));
  }, [tab, mascota, refreshVacunas]);

  // --- Certificado de salud (tipo el que piden aerolíneas) ---
  const [certificadosSalud, setCertificadosSalud] = useState<any[]>([]);
  const [cargandoCertificados, setCargandoCertificados] = useState(true);
  const [modalCertificadoAbierto, setModalCertificadoAbierto] = useState(false);
  const [formCertificado, setFormCertificado] = useState({
    veterinarioId: "",
    desparasitacion: "",
    vacunasIds: [] as string[],
    declaracionConfirmada: false,
  });
  const [guardandoCertificado, setGuardandoCertificado] = useState(false);
  const [errorCertificado, setErrorCertificado] = useState<string | null>(null);
  const [refreshCertificados, setRefreshCertificados] = useState(0);

  useEffect(() => {
    if (tab !== "certificado-salud" || !mascota) return;
    setCargandoCertificados(true);
    api
      .get(`/mascotas/${mascota.id}/certificados-salud`)
      .then((res) => setCertificadosSalud(res.data.items ?? []))
      .finally(() => setCargandoCertificados(false));
  }, [tab, mascota, refreshCertificados]);

  function abrirGenerarCertificado() {
    setFormCertificado({ veterinarioId: "", desparasitacion: "", vacunasIds: [], declaracionConfirmada: false });
    setErrorCertificado(null);
    setModalCertificadoAbierto(true);
  }

  function alternarVacunaCertificado(id: string) {
    setFormCertificado((f) => ({
      ...f,
      vacunasIds: f.vacunasIds.includes(id) ? f.vacunasIds.filter((v) => v !== id) : [...f.vacunasIds, id],
    }));
  }

  async function generarCertificado(e: FormEvent) {
    e.preventDefault();
    setGuardandoCertificado(true);
    setErrorCertificado(null);
    try {
      await api.post(`/mascotas/${mascota.id}/certificado-salud`, formCertificado);
      setModalCertificadoAbierto(false);
      setRefreshCertificados((v) => v + 1);
    } catch (err) {
      setErrorCertificado(mensajeError(err));
    } finally {
      setGuardandoCertificado(false);
    }
  }

  function abrirCrearVacuna() {
    setVacunaEditando(null);
    setFormVacuna({
      veterinarioId: "",
      nombre: "",
      lote: "",
      farmaceutica: "",
      viaAplicacion: "",
      fechaAplicacion: "",
      sinProximaDosis: false,
      fechaProximaDosis: "",
      recordatorioAutomatico: false,
    });
    setErrorFormVacuna(null);
    setModalVacunaAbierto(true);
  }

  function abrirEditarVacuna(v: any) {
    setVacunaEditando(v);
    setFormVacuna({
      veterinarioId: v.veterinarioId ?? "",
      nombre: v.nombre ?? "",
      lote: v.lote ?? "",
      farmaceutica: v.farmaceutica ?? "",
      viaAplicacion: v.viaAplicacion ?? "",
      fechaAplicacion: v.fechaAplicacion ? aFechaSoloDiaInput(v.fechaAplicacion) : "",
      sinProximaDosis: !v.fechaProximaDosis,
      fechaProximaDosis: v.fechaProximaDosis ? aFechaSoloDiaInput(v.fechaProximaDosis) : "",
      recordatorioAutomatico: !!v.recordatorioAutomatico,
    });
    setErrorFormVacuna(null);
    setModalVacunaAbierto(true);
  }

  async function guardarVacuna(e: FormEvent) {
    e.preventDefault();
    setGuardandoVacuna(true);
    setErrorFormVacuna(null);
    try {
      const payload: Record<string, any> = {
        mascotaId: mascota.id,
        veterinarioId: formVacuna.veterinarioId,
        nombre: formVacuna.nombre,
        lote: formVacuna.lote || undefined,
        farmaceutica: formVacuna.farmaceutica || undefined,
        viaAplicacion: formVacuna.viaAplicacion || undefined,
        fechaAplicacion: formVacuna.fechaAplicacion,
        fechaProximaDosis: formVacuna.sinProximaDosis ? null : formVacuna.fechaProximaDosis || null,
        recordatorioAutomatico: formVacuna.sinProximaDosis ? false : formVacuna.recordatorioAutomatico,
      };
      if (vacunaEditando) {
        await api.put(`/vacunas/${vacunaEditando.id}`, payload);
      } else {
        await api.post("/vacunas", payload);
      }
      setModalVacunaAbierto(false);
      setRefreshVacunas((v) => v + 1);
    } catch (err) {
      setErrorFormVacuna(mensajeError(err));
    } finally {
      setGuardandoVacuna(false);
    }
  }

  async function eliminarVacuna(v: any) {
    if (!confirm(`¿Seguro que quieres eliminar el registro de la vacuna "${v.nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/vacunas/${v.id}`);
      setRefreshVacunas((n) => n + 1);
    } catch (err) {
      alert(mensajeError(err));
    }
  }

  useEffect(() => {
    if (!id) return;
    api
      .get(`/mascotas/${id}`)
      .then((res) => setMascota(res.data))
      .catch((err) => setError(mensajeError(err)));
  }, [id]);

  // El enlace público del expediente se genera solo la primera vez que el
  // admin abre esta ficha, así siempre está listo para compartir o para
  // enlazar los certificados de vacuna, aunque la mascota se haya dado de
  // alta antes de que existiera esta función.
  useEffect(() => {
    if (!mascota || mascota.tokenPublico) return;
    api
      .post(`/mascotas/${mascota.id}/generar-link`)
      .then((res) => setMascota((m: any) => ({ ...m, tokenPublico: res.data.tokenPublico })))
      .catch(() => {
        /* si falla, el botón de compartir simplemente no aparece */
      });
  }, [mascota]);

  useEffect(() => {
    if (!id) return;
    api
      .get(`/mascotas/${id}/expediente`)
      .then((res) => setExpediente(res.data.items))
      .catch((err) => setErrorExpediente(mensajeError(err)));
  }, [id, tab, refreshVacunas]);

  function copiarEnlace() {
    if (!mascota?.tokenPublico) return;
    const link = `${window.location.origin}/portal-mascota/${mascota.tokenPublico}`;
    navigator.clipboard.writeText(link).then(() => {
      setLinkCopiado(true);
      setTimeout(() => setLinkCopiado(false), 2000);
    });
  }

  function abrirConfirmarVacuna(vacuna: any) {
    setVacunaAConfirmar(vacuna);
    setConfirmNombre(vacuna.nombre ?? "");
    setConfirmFecha(vacuna.fechaAplicacion ? aFechaSoloDiaInput(vacuna.fechaAplicacion) : "");
    setConfirmError(null);
  }

  async function guardarConfirmacionVacuna(e: FormEvent) {
    e.preventDefault();
    if (!vacunaAConfirmar) return;
    setConfirmGuardando(true);
    setConfirmError(null);
    try {
      await api.put(`/vacunas/${vacunaAConfirmar.id}/confirmar`, {
        nombre: confirmNombre,
        fechaAplicacion: confirmFecha,
      });
      setVacunaAConfirmar(null);
      setRefreshVacunas((v) => v + 1);
    } catch (err) {
      setConfirmError(mensajeError(err));
    } finally {
      setConfirmGuardando(false);
    }
  }

  async function resolverSolicitudEliminacion(accion: "descartar" | "desactivar") {
    if (accion === "desactivar" && !confirm(`¿Marcar a ${mascota.nombre} como inactiva? El historial no se borra.`)) return;
    try {
      const res = await api.post(`/mascotas/${mascota.id}/resolver-solicitud-eliminacion`, { accion });
      setMascota(res.data);
    } catch (err) {
      alert(mensajeError(err));
    }
  }

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!mascota) return <div className="flex justify-center py-16"><Spinner className="h-8 w-8" /></div>;

  return (
    <div>
      <button className="btn-ghost mb-4 !px-2" onClick={() => navigate("/mascotas")}>
        <ArrowLeft size={16} /> Volver a mascotas
      </button>

      <div className="card mb-6 flex flex-wrap items-center gap-4 p-5">
        <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl bg-brand-50 text-brand-500">
          {mascota.fotoUrl ? (
            <img src={mascota.fotoUrl} alt={mascota.nombre} className="h-full w-full object-cover" />
          ) : (
            <PawPrint size={26} />
          )}
        </div>
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-slate-800">{mascota.nombre}</h1>
          <p className="text-sm text-slate-400">
            {mascota.especie} · {mascota.raza ?? "Sin raza especificada"} · Tutor: {mascota.cliente?.nombre}
          </p>
        </div>
        {mascota.tokenPublico && (
          <button className="btn-secondary !text-xs" onClick={copiarEnlace} title="Copiar enlace del expediente para el cliente">
            {linkCopiado ? <Check size={14} /> : <Link2 size={14} />}
            {linkCopiado ? "¡Copiado!" : "Compartir expediente"}
          </button>
        )}
        <button
          className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
          onClick={() => setMostrarModalBaja(true)}
          title="Dar de baja del negocio (no borra el expediente)"
        >
          <UserX size={14} /> Dar de baja
        </button>
        <Badge color={mascota.activo ? "verde" : "gris"}>{mascota.activo ? "Activo" : "Inactivo"}</Badge>
      </div>

      {mascota.solicitudEliminacion && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800">
          <p>
            El cliente solicitó desde el portal público que se elimine el expediente de <strong>{mascota.nombre}</strong>.
          </p>
          <div className="flex gap-2">
            <button className="btn-secondary !py-1.5 !text-xs" onClick={() => resolverSolicitudEliminacion("descartar")}>
              Descartar solicitud
            </button>
            <button
              className="rounded-xl bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700"
              onClick={() => resolverSolicitudEliminacion("desactivar")}
            >
              Marcar como inactiva
            </button>
          </div>
        </div>
      )}

      <div className="mb-4 flex gap-2 overflow-x-auto border-b border-slate-100">
        {TABS.map((t) => (
          <button
            key={t.id}
            className={`shrink-0 whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium ${
              tab === t.id ? "border-brand-500 text-brand-600" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "consultas" && (
        <CrudPage
          resource="/consultas"
          seccion="mascotas"
          titulo="Consultas médicas"
          nombreSingular="consulta"
          filtrosFijos={{ mascotaId: mascota.id }}
          campos={[
            { name: "veterinarioId", label: "Veterinario", type: "select", options: veterinarios, required: true },
            { name: "fecha", label: "Fecha", type: "datetime-local" },
            { name: "motivo", label: "Motivo de consulta", required: true, colSpan2: true },
            { name: "diagnostico", label: "Diagnóstico", type: "textarea", colSpan2: true },
            { name: "tratamiento", label: "Tratamiento", type: "textarea", colSpan2: true },
            { name: "peso", label: "Peso (kg)", type: "number" },
            { name: "temperatura", label: "Temperatura (°C)", type: "number" },
            { name: "notas", label: "Notas", type: "textarea", colSpan2: true },
          ]}
          columnas={[
            { key: "fecha", label: "Fecha", render: (r: any) => new Date(r.fecha).toLocaleDateString("es-MX") },
            { key: "motivo", label: "Motivo" },
            { key: "diagnostico", label: "Diagnóstico" },
            { key: "veterinario", label: "Veterinario", render: (r: any) => r.veterinario?.nombre },
          ]}
        />
      )}

      {tab === "vacunas" && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-slate-700">Vacunas aplicadas</h2>
              <p className="text-sm text-slate-500">
                El certificado imprimible de cada vacuna solo se habilita cuando confirmas manualmente que se aplicó.
              </p>
            </div>
            <button className="btn-primary" onClick={abrirCrearVacuna}>
              <Plus size={16} /> Nueva vacuna
            </button>
          </div>

          <div className="card overflow-x-auto">
            {cargandoVacunas ? (
              <div className="flex justify-center py-16"><Spinner /></div>
            ) : errorVacunas ? (
              <div className="p-6 text-sm text-red-600">{errorVacunas}</div>
            ) : vacunas.length === 0 ? (
              <EmptyState titulo="No hay vacunas registradas todavía" descripcion="Crea la primera usando el botón de arriba." />
            ) : (
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400">
                    <th className="px-4 py-3 font-medium">Vacuna</th>
                    <th className="px-4 py-3 font-medium">Aplicada</th>
                    <th className="px-4 py-3 font-medium">Próxima dosis</th>
                    <th className="px-4 py-3 font-medium">Certificado</th>
                    <th className="px-4 py-3 text-right font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {vacunas.map((r: any) => (
                    <tr key={r.id} className="border-b border-slate-50 last:border-0 align-top">
                      <td className="px-4 py-3 text-slate-700">
                        <p className="font-medium">{r.nombre}</p>
                        {r.lote && <p className="text-xs text-slate-400">Lote {r.lote}</p>}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{new Date(r.fechaAplicacion).toLocaleDateString("es-MX")}</td>
                      <td className="px-4 py-3 text-slate-700">
                        {r.fechaProximaDosis ? (
                          <>
                            <p>{new Date(r.fechaProximaDosis).toLocaleDateString("es-MX")}</p>
                            {r.recordatorioAutomatico && (
                              <p className="mt-0.5 flex items-center gap-1 text-xs text-brand-600">
                                <Bell size={11} /> {r.recordatorioGenerado ? "Recordatorio enviado" : "Recordatorio programado"}
                              </p>
                            )}
                          </>
                        ) : (
                          <span className="text-slate-400">No aplica</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {r.confirmada ? (
                          mascota.tokenPublico ? (
                            <a
                              href={`/portal-mascota/${mascota.tokenPublico}/vacuna/${r.id}/certificado`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:underline"
                            >
                              <Award size={14} /> Ver certificado
                            </a>
                          ) : (
                            <span className="text-xs text-slate-400">Confirmada</span>
                          )
                        ) : (
                          <button className="btn-secondary !py-1 !text-xs" onClick={() => abrirConfirmarVacuna(r)}>
                            Confirmar aplicación
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <button className="btn-ghost !px-2" title="Editar" onClick={() => abrirEditarVacuna(r)}>
                            <Pencil size={15} />
                          </button>
                          <button className="btn-ghost !px-2 text-red-500" title="Eliminar" onClick={() => eliminarVacuna(r)}>
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Modal: nueva/editar vacuna */}
      <Modal
        open={modalVacunaAbierto}
        onClose={() => setModalVacunaAbierto(false)}
        title={vacunaEditando ? "Editar vacuna" : "Nueva vacuna"}
      >
        <form onSubmit={guardarVacuna} className="space-y-4">
          <div>
            <label className="label">Veterinario</label>
            <select
              className="input"
              required
              value={formVacuna.veterinarioId}
              onChange={(e) => setFormVacuna({ ...formVacuna, veterinarioId: e.target.value })}
            >
              <option value="">Selecciona...</option>
              {veterinarios.map((o: any) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Nombre de la vacuna</label>
            <input
              className="input"
              required
              value={formVacuna.nombre}
              onChange={(e) => setFormVacuna({ ...formVacuna, nombre: e.target.value })}
            />
          </div>
          <div>
            <label className="label">Lote</label>
            <input
              className="input"
              value={formVacuna.lote}
              onChange={(e) => setFormVacuna({ ...formVacuna, lote: e.target.value })}
            />
          </div>
          <div>
            <label className="label">Farmacéutica / laboratorio</label>
            <input
              className="input"
              placeholder="Ej. Zoetis, MSD, Virbac"
              value={formVacuna.farmaceutica}
              onChange={(e) => setFormVacuna({ ...formVacuna, farmaceutica: e.target.value })}
            />
          </div>
          <div>
            <label className="label">Vía de aplicación</label>
            <select
              className="input"
              value={formVacuna.viaAplicacion}
              onChange={(e) => setFormVacuna({ ...formVacuna, viaAplicacion: e.target.value })}
            >
              <option value="">Selecciona...</option>
              <option value="Subcutánea">Subcutánea</option>
              <option value="Intramuscular">Intramuscular</option>
              <option value="Oral">Oral</option>
              <option value="Intranasal">Intranasal</option>
              <option value="Otra">Otra</option>
            </select>
          </div>
          <div>
            <label className="label">Fecha de aplicación</label>
            <input
              type="date"
              className="input"
              required
              value={formVacuna.fechaAplicacion}
              onChange={(e) => setFormVacuna({ ...formVacuna, fechaAplicacion: e.target.value })}
            />
          </div>

          <div className="rounded-xl border border-slate-100 p-3">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-brand-500 focus:ring-brand-400"
                checked={formVacuna.sinProximaDosis}
                onChange={(e) =>
                  setFormVacuna({
                    ...formVacuna,
                    sinProximaDosis: e.target.checked,
                    ...(e.target.checked ? { fechaProximaDosis: "", recordatorioAutomatico: false } : {}),
                  })
                }
              />
              No aplica próxima dosis
            </label>

            {!formVacuna.sinProximaDosis && (
              <div className="mt-3 space-y-3">
                <div>
                  <label className="label">Fecha de próxima dosis</label>
                  <input
                    type="date"
                    className="input"
                    value={formVacuna.fechaProximaDosis}
                    onChange={(e) => setFormVacuna({ ...formVacuna, fechaProximaDosis: e.target.value })}
                  />
                </div>
                {formVacuna.fechaProximaDosis && (
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-brand-500 focus:ring-brand-400"
                      checked={formVacuna.recordatorioAutomatico}
                      onChange={(e) => setFormVacuna({ ...formVacuna, recordatorioAutomatico: e.target.checked })}
                    />
                    Crear recordatorio automático (WhatsApp/correo) cuando se acerque la fecha
                  </label>
                )}
              </div>
            )}
          </div>

          {errorFormVacuna && <p className="text-sm text-red-600">{errorFormVacuna}</p>}
          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <button type="button" className="btn-secondary" onClick={() => setModalVacunaAbierto(false)}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={guardandoVacuna}>
              {guardandoVacuna ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </form>
      </Modal>

      {tab === "recetas" && (
        <CrudPage
          resource="/recetas"
          seccion="mascotas"
          titulo="Recetas digitales"
          nombreSingular="receta"
          filtrosFijos={{ mascotaId: mascota.id }}
          campos={[
            { name: "veterinarioId", label: "Veterinario", type: "select", options: veterinarios, required: true },
            {
              name: "medicamentos",
              label: "Medicamentos",
              type: "custom",
              colSpan2: true,
              default: [{ nombre: "", dosis: "", frecuencia: "", duracion: "" }],
              render: (valores, onChange) => <CampoMedicamentos valores={valores} onChange={onChange} />,
            },
            { name: "indicaciones", label: "Indicaciones", type: "textarea", colSpan2: true },
          ]}
          columnas={[
            { key: "fecha", label: "Fecha", render: (r: any) => new Date(r.fecha).toLocaleDateString("es-MX") },
            {
              key: "medicamentos",
              label: "Medicamentos",
              render: (r: any) => (Array.isArray(r.medicamentos) ? r.medicamentos.map((m: any) => m.nombre).join(", ") : "—"),
            },
            { key: "veterinario", label: "Veterinario", render: (r: any) => r.veterinario?.nombre },
            {
              key: "documento",
              label: "Documento",
              render: (r: any) => (
                <div className="flex flex-wrap items-center gap-3">
                  {mascota.tokenPublico ? (
                    <a
                      href={`/portal-mascota/${mascota.tokenPublico}/receta/${r.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:underline"
                    >
                      <Printer size={14} /> Ver/imprimir
                    </a>
                  ) : (
                    <span className="text-xs text-slate-400">Comparte el expediente primero</span>
                  )}
                  <button
                    type="button"
                    disabled={enviandoRecetaId === r.id}
                    onClick={() => enviarRecetaCorreo(r.id)}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-brand-700 disabled:opacity-50"
                  >
                    {enviandoRecetaId === r.id ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                    Enviar por correo
                  </button>
                </div>
              ),
            },
          ]}
        />
      )}

      {tab === "documentos" && (
        <CrudPage
          resource="/documentos"
          seccion="mascotas"
          titulo="Documentos y estudios"
          descripcion="Toma una fotografía de radiografías, laboratorios, ultrasonidos u otros estudios y quedará guardada aquí, en el expediente de la mascota. La foto se optimiza automáticamente para ocupar el mínimo espacio posible."
          nombreSingular="documento"
          filtrosFijos={{ mascotaId: mascota.id }}
          campos={[
            {
              name: "tipo",
              label: "Tipo",
              type: "select",
              options: [
                { value: "RADIOGRAFIA", label: "Radiografía" },
                { value: "ANALISIS_LABORATORIO", label: "Análisis de laboratorio" },
                { value: "ULTRASONIDO", label: "Ultrasonido" },
                { value: "FOTO", label: "Foto" },
                { value: "OTRO", label: "Otro" },
              ],
            },
            {
              name: "url",
              label: "Fotografía del documento o estudio",
              type: "image",
              required: true,
              colSpan2: true,
              imagenLadoMaximo: 1100,
              imagenCalidad: 0.6,
            },
            { name: "descripcion", label: "Descripción", type: "textarea", colSpan2: true },
          ]}
          columnas={[
            { key: "tipo", label: "Tipo" },
            { key: "descripcion", label: "Descripción" },
            {
              key: "url",
              label: "Archivo",
              render: (r: any) => (
                <a href={r.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-brand-600 hover:underline">
                  {typeof r.url === "string" && r.url.startsWith("data:image") && (
                    <img src={r.url} alt="" className="h-8 w-8 rounded object-cover" />
                  )}
                  Ver
                </a>
              ),
            },
          ]}
        />
      )}

      {tab === "certificado-salud" && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-slate-700">Certificado de salud</h2>
              <p className="text-sm text-slate-500">
                Documento formal (tipo el que piden las aerolíneas) con los datos de la mascota, del propietario, la
                declaración de salud y las vacunas vigentes.
              </p>
            </div>
            <button className="btn-primary" onClick={abrirGenerarCertificado}>
              <Award size={16} /> Generar certificado
            </button>
          </div>

          <div className="card overflow-x-auto">
            {cargandoCertificados ? (
              <div className="flex justify-center py-16"><Spinner /></div>
            ) : certificadosSalud.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400">Todavía no se ha generado ningún certificado.</div>
            ) : (
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400">
                    <th className="px-4 py-3 font-medium">Fecha de emisión</th>
                    <th className="px-4 py-3 font-medium">Veterinario</th>
                    <th className="px-4 py-3 text-right font-medium">Certificado</th>
                  </tr>
                </thead>
                <tbody>
                  {certificadosSalud.map((c: any) => (
                    <tr key={c.id} className="border-b border-slate-50 last:border-0">
                      <td className="px-4 py-3 text-slate-700">{new Date(c.fechaEmision).toLocaleDateString("es-MX")}</td>
                      <td className="px-4 py-3 text-slate-700">
                        {c.veterinario?.titulo ? `${c.veterinario.titulo} ` : ""}
                        {c.veterinario?.nombre}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {mascota.tokenPublico ? (
                          <a
                            href={`/portal-mascota/${mascota.tokenPublico}/certificado-salud/${c.id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:underline"
                          >
                            <Award size={14} /> Ver certificado
                          </a>
                        ) : (
                          <span className="text-xs text-slate-400">Comparte el expediente primero</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Modal: generar certificado de salud */}
      <Modal open={modalCertificadoAbierto} onClose={() => setModalCertificadoAbierto(false)} title="Generar certificado de salud">
        <form onSubmit={generarCertificado} className="space-y-4">
          <div>
            <label className="label">Veterinario que certifica</label>
            <select
              className="input"
              required
              value={formCertificado.veterinarioId}
              onChange={(e) => setFormCertificado({ ...formCertificado, veterinarioId: e.target.value })}
            >
              <option value="">Selecciona...</option>
              {veterinarios.map((o: any) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Vacunas a incluir como vigentes</label>
            {vacunas.filter((v: any) => v.confirmada).length === 0 ? (
              <p className="text-xs text-slate-400">
                No hay vacunas confirmadas todavía. Confirma la aplicación desde la pestaña Vacunas para poder incluirlas
                aquí.
              </p>
            ) : (
              <div className="max-h-40 space-y-1.5 overflow-y-auto rounded-xl border border-slate-100 p-3">
                {vacunas
                  .filter((v: any) => v.confirmada)
                  .map((v: any) => (
                    <label key={v.id} className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-brand-500 focus:ring-brand-400"
                        checked={formCertificado.vacunasIds.includes(v.id)}
                        onChange={() => alternarVacunaCertificado(v.id)}
                      />
                      {v.nombre} — {new Date(v.fechaAplicacion).toLocaleDateString("es-MX")}
                    </label>
                  ))}
              </div>
            )}
          </div>

          <div>
            <label className="label">Desparasitación</label>
            <input
              className="input"
              placeholder="Ej. Producto y fecha de la última desparasitación"
              value={formCertificado.desparasitacion}
              onChange={(e) => setFormCertificado({ ...formCertificado, desparasitacion: e.target.value })}
            />
          </div>

          <label className="flex items-start gap-2 rounded-xl border border-amber-100 bg-amber-50/60 p-3 text-sm text-slate-700">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-500 focus:ring-brand-400"
              checked={formCertificado.declaracionConfirmada}
              onChange={(e) => setFormCertificado({ ...formCertificado, declaracionConfirmada: e.target.checked })}
            />
            Certifico que {mascota.nombre} está libre de enfermedades infectocontagiosas, libre de parásitos, no
            presenta heridas recientes y es apta para viajar.
          </label>

          {errorCertificado && <p className="text-sm text-red-600">{errorCertificado}</p>}
          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <button type="button" className="btn-secondary" onClick={() => setModalCertificadoAbierto(false)}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={guardandoCertificado || !formCertificado.declaracionConfirmada}>
              {guardandoCertificado ? "Generando..." : "Generar certificado"}
            </button>
          </div>
        </form>
      </Modal>

      <div className="mt-8">
        <div className="mb-3 flex items-center gap-2">
          <ClipboardList size={18} className="text-slate-400" />
          <h2 className="text-base font-semibold text-slate-800">Expediente completo</h2>
        </div>
        <p className="mb-4 text-sm text-slate-400">
          Historial de todas las actividades registradas para {mascota.nombre}: citas, consultas, vacunas, recetas,
          documentos, estética, servicios a domicilio, paseos, entrenamiento y hospedajes — en orden cronológico.
        </p>

        {errorExpediente && <p className="text-sm text-red-600">{errorExpediente}</p>}

        {!errorExpediente && expediente === null && (
          <div className="flex justify-center py-10">
            <Spinner className="h-6 w-6" />
          </div>
        )}

        {expediente !== null && expediente.length === 0 && (
          <div className="card p-6 text-center text-sm text-slate-400">
            Todavía no hay actividades registradas para esta mascota.
          </div>
        )}

        {expediente !== null && expediente.length > 0 && (
          <ol className="relative border-l border-slate-200 pl-6">
            {expediente.map((ev) => {
              const Icono = ICONOS_EVENTO[ev.tipo] ?? ClipboardList;
              const colorIcono = COLORES_EVENTO[ev.tipo] ?? "bg-slate-100 text-slate-600";
              return (
                <li key={ev.id} className="mb-6 last:mb-0">
                  <span
                    className={`absolute -left-[15px] flex h-7 w-7 items-center justify-center rounded-full ring-4 ring-white ${colorIcono}`}
                  >
                    <Icono size={14} />
                  </span>
                  <div className="card p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{ev.tipo}</p>
                        <p className="font-medium text-slate-800">{ev.titulo}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {ev.estado && <Badge color={COLORES_ESTADO_BADGE[ev.estado] ?? "gris"}>{ev.estado}</Badge>}
                        <span className="whitespace-nowrap text-xs text-slate-400">
                          {new Date(ev.fecha).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" })}
                        </span>
                      </div>
                    </div>
                    {ev.detalle && <p className="mt-2 text-sm text-slate-600">{ev.detalle}</p>}
                    {ev.responsable && <p className="mt-1 text-xs text-slate-400">Atendió: {ev.responsable}</p>}
                    {ev.tipo === "Vacuna" && ev.certificadoDisponible && ev.vacunaId && mascota.tokenPublico && (
                      <a
                        href={`/portal-mascota/${mascota.tokenPublico}/vacuna/${ev.vacunaId}/certificado`}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:underline"
                      >
                        <Award size={14} /> Ver certificado de vacunación
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <Modal open={!!vacunaAConfirmar} onClose={() => setVacunaAConfirmar(null)} title="Confirmar aplicación de vacuna">
        <form onSubmit={guardarConfirmacionVacuna} className="space-y-4">
          <p className="text-sm text-slate-500">
            Esta confirmación habilita el certificado imprimible para el cliente. Escribe el nombre exacto de la vacuna y
            la fecha en que se aplicó.
          </p>
          <div>
            <label className="label">Nombre exacto de la vacuna</label>
            <input className="input" required value={confirmNombre} onChange={(e) => setConfirmNombre(e.target.value)} />
          </div>
          <div>
            <label className="label">Fecha de aplicación</label>
            <input type="date" className="input" required value={confirmFecha} onChange={(e) => setConfirmFecha(e.target.value)} />
          </div>
          {confirmError && <p className="text-sm text-red-600">{confirmError}</p>}
          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <button type="button" className="btn-secondary" onClick={() => setVacunaAConfirmar(null)}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={confirmGuardando}>
              {confirmGuardando && <Spinner className="h-4 w-4 text-white" />}
              Confirmar y habilitar certificado
            </button>
          </div>
        </form>
      </Modal>

      <ModalBajaMascota
        mascota={mostrarModalBaja ? mascota : null}
        onClose={() => setMostrarModalBaja(false)}
        onBajaExitosa={() => {
          setMostrarModalBaja(false);
          // Ya no aparece en el panel de este negocio, así que regresamos al
          // listado en vez de dejar al usuario en una ficha que ya no podrá
          // volver a abrir.
          navigate("/mascotas");
        }}
      />
    </div>
  );
}
